import React from "react";
import Card from "../ui/Card";
import type { PerformanceSummary, SlaSummary, EliteSummary, TimingSummary, SlaDetailGroup } from "../../types/performance";

type Props = {
  summary: PerformanceSummary;
  sla: SlaSummary;
  elite: EliteSummary;
  timing: TimingSummary;
  onSelectSlaGroup: (group: SlaDetailGroup, label: string) => void;
};

// Primera fila de KPIs — jerarquía inspirada en "Mis métricas" de
// MercadoLibre (sin copiar su diseño): pocas cards grandes, el
// número/porcentaje principal domina visualmente. "SLA correcto" reemplaza
// a "Envíos correctos": ya no es delivered/total, es
// (Elite+OnTime)/evaluables — ver server/legacy/performance/performance.sla.js.
const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "16px",
};

const cardStyle = (clickable: boolean): React.CSSProperties => ({
  cursor: clickable ? "pointer" : "default",
});

const labelStyle: React.CSSProperties = {
  fontSize: "14px",
  fontWeight: 600,
  color: "var(--text-secondary)",
  marginBottom: "14px",
};

const valueStyle: React.CSSProperties = {
  fontSize: "40px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.04em",
  lineHeight: 1,
};

const metaStyle: React.CSSProperties = {
  marginTop: "14px",
  fontSize: "14px",
  color: "var(--text-secondary)",
};

const formatPercent = (rate: number) => `${(rate * 100).toFixed(1)}%`;
const formatInt = (value: number) => new Intl.NumberFormat("es-AR").format(value);

const PerformanceSummaryCards = ({ summary, sla, elite, timing, onSelectSlaGroup }: Props) => {
  const lateCount = timing.deliveredLate + timing.confirmedDelayed;

  return (
    <div style={gridStyle} className="performance-kpi-grid">
      <div
        style={cardStyle(sla.eligible > 0)}
        onClick={sla.eligible > 0 ? () => onSelectSlaGroup("ok", "SLA correcto") : undefined}
      >
        <Card>
          <div style={labelStyle}>SLA correcto</div>
          <div style={valueStyle}>{formatPercent(sla.rate)}</div>
          <div style={metaStyle}>{formatInt(sla.ok)} de {formatInt(sla.eligible)} envíos evaluables.</div>
        </Card>
      </div>

      <Card>
        <div style={labelStyle}>Cantidad de envíos</div>
        <div style={valueStyle}>{formatInt(summary.total)}</div>
        <div style={metaStyle}>Total del período (incluye excluidos y pendientes).</div>
      </Card>

      <div
        style={cardStyle(elite.count > 0)}
        onClick={elite.count > 0 ? () => onSelectSlaGroup("elite", "Elite") : undefined}
      >
        <Card>
          <div style={labelStyle}>Elite</div>
          <div style={valueStyle}>{formatPercent(elite.rate)}</div>
          <div style={metaStyle}>{formatInt(elite.count)} envío(s) — entregados antes de las 21 hs.</div>
        </Card>
      </div>

      <div
        style={cardStyle(lateCount > 0)}
        onClick={lateCount > 0 ? () => onSelectSlaGroup("late", "Demorados") : undefined}
      >
        <Card>
          <div style={labelStyle}>Demorados</div>
          <div style={valueStyle}>{formatInt(lateCount)}</div>
          <div style={metaStyle}>{formatPercent(sla.eligible ? lateCount / sla.eligible : 0)} de los evaluables — entregado ≥23 hs o demora confirmada.</div>
        </Card>
      </div>

      <style>
        {`
          @media (max-width: 1180px) {
            .performance-kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          }
          @media (max-width: 720px) {
            .performance-kpi-grid { grid-template-columns: 1fr !important; }
          }
        `}
      </style>
    </div>
  );
};

export default PerformanceSummaryCards;
